import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { getNetlifyFunctionsBase } from "../utils/netlifyFunctions.js";
import { buildTranslationFieldsFromQuickClarityInsight } from "../utils/insightTranslationFields.js";

function tCacheKey(id, lang) {
  return `${id}::${lang}`;
}

/**
 * English / Hindi (or other) display translation for a Quick Clarity insight.
 */
export function useInsightTranslation(insight, { cacheId } = {}) {
  const id = cacheId ?? insight?.id ?? "live";
  const [displayLang, setDisplayLang] = useState("en");
  const [translationCache, setTranslationCache] = useState({});
  const [translationError, setTranslationError] = useState("");
  const inFlightRef = useRef(new Set());

  const fields = useMemo(() => buildTranslationFieldsFromQuickClarityInsight(insight), [insight]);
  const fieldsKey = useMemo(() => JSON.stringify(fields), [fields]);

  const runTranslation = useCallback(
    async (targetLang) => {
      if (targetLang === "en") return;
      const key = tCacheKey(id, targetLang);
      if (inFlightRef.current.has(key)) return;

      const parsedFields = JSON.parse(fieldsKey);
      if (Object.keys(parsedFields).length === 0) {
        setTranslationCache((prev) => ({ ...prev, [key]: {} }));
        return;
      }

      inFlightRef.current.add(key);
      setTranslationError("");
      try {
        const response = await fetch(`${getNetlifyFunctionsBase()}/.netlify/functions/translateHistoryContent`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fields: parsedFields, targetLang })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.error || "Translation failed");
        }
        setTranslationCache((prev) => ({ ...prev, [key]: data.translations || {} }));
      } catch (err) {
        console.error("useInsightTranslation", err);
        setTranslationError(err?.message || "Translation failed");
        setTranslationCache((prev) => ({ ...prev, [key]: {} }));
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [fieldsKey, id]
  );

  useEffect(() => {
    if (displayLang === "en") return;
    const key = tCacheKey(id, displayLang);
    if (translationCache[key] !== undefined || inFlightRef.current.has(key)) return;
    runTranslation(displayLang);
  }, [displayLang, fieldsKey, id, runTranslation, translationCache]);

  const tx = useCallback(
    (key, original) => {
      if (displayLang === "en" || original == null || original === "") return original;
      const block = translationCache[tCacheKey(id, displayLang)];
      if (!block || block[key] == null || block[key] === "") return original;
      return block[key];
    },
    [displayLang, id, translationCache]
  );

  const txList = useCallback(
    (key, originalList) => {
      if (displayLang === "en" || !Array.isArray(originalList)) return originalList;
      const block = translationCache[tCacheKey(id, displayLang)];
      const translated = block?.[key];
      if (!Array.isArray(translated) || translated.length !== originalList.length) return originalList;
      return translated;
    },
    [displayLang, id, translationCache]
  );

  const isTranslating =
    displayLang !== "en" &&
    translationCache[tCacheKey(id, displayLang)] === undefined &&
    Object.keys(fields).length > 0;

  const retryTranslation = useCallback(() => {
    if (displayLang === "en") return;
    const key = tCacheKey(id, displayLang);
    setTranslationError("");
    inFlightRef.current.delete(key);
    setTranslationCache((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [displayLang, id]);

  return {
    displayLang,
    setDisplayLang,
    tx,
    txList,
    isTranslating,
    translationError,
    retryTranslation
  };
}
